import { formatList, OptionCards } from '@platform/design-system';
import { useState } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { QueryView } from '../QueryView.tsx';
import { canDispatchWith, useCarriers } from '../settings/carriers.ts';
import { shopSummaryOf } from '../shop-detail.ts';
import { usePageContext } from '../usePageContext.ts';
import { useBranding } from './branding.ts';
import type { StepViewProps } from './step-flow.ts';
import type { TenantView } from './store-profile.ts';
import styles from './Setup.module.css';

const WINDOW_CHOICES: readonly number[] = [24, 48, 72];

const windowChoicesOf = (current: number): readonly number[] =>
  WINDOW_CHOICES.includes(current) ? WINDOW_CHOICES : [...WINDOW_CHOICES, current];

const SetupSummary = ({
  api,
  shipping,
  tenant,
  hours,
}: StepViewProps & { tenant: TenantView; hours: number }) => {
  const { t, locale } = usePageContext();
  const branding = useBranding(api);
  const carriers = useCarriers(shipping);
  const connected = (carriers.data?.items ?? []).filter(canDispatchWith).map((carrier) => carrier.name);
  const rows = [
    [t('setup.step_store'), shopSummaryOf(tenant, locale)],
    [
      t('setup.step_brand'),
      branding.data?.colors === null || branding.data === undefined
        ? t('setup.summaryUnbranded')
        : t('setup.summaryBranded'),
    ],
    [
      t('setup.step_delivery'),
      connected.length === 0 ? t('setup.summaryNoCarrier') : formatList(connected, locale),
    ],
    [t('setup.window'), t('setup.windowHours', { count: hours })],
  ] as const;
  return (
    <section className={styles['summary']} aria-labelledby="setup-summary">
      <h2 id="setup-summary" className={styles['legend']}>
        {t('setup.summaryTitle')}
      </h2>
      <dl>
        {rows.map(([term, value]) => (
          <div key={term} className={styles['summaryRow']}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const OrdersForm = (props: StepViewProps & { tenant: TenantView }) => {
  const { api, flow, tenant } = props;
  const { t, invalidate } = usePageContext();
  const current = tenant.orderConfirmationTtlHours;
  const [hours, setHours] = useState(current);
  return (
    <form
      id={flow.formId}
      className={styles['stack']}
      onSubmit={(event) => {
        event.preventDefault();
        flow.submit(async () => {
          await api.put('/admin/tenant/order-settings', { body: { orderConfirmationTtlHours: hours } });
          await invalidate('tenant');
        });
      }}
    >
      <OptionCards
        label={t('setup.window')}
        columns={WINDOW_CHOICES.includes(current) ? 3 : 4}
        value={String(hours)}
        onChange={(value) => {
          setHours(Number(value));
        }}
        options={windowChoicesOf(current).map((choice) => ({
          value: String(choice),
          title: t('setup.windowHours', { count: choice }),
          description: WINDOW_CHOICES.includes(choice)
            ? t(`setup.windowHelp_${String(choice)}`)
            : t('setup.windowHelp_current'),
        }))}
      />
      <SetupSummary {...props} hours={hours} />
    </form>
  );
};

export const OrdersStep = (props: StepViewProps) => {
  const { t } = usePageContext();
  return (
    <QueryView query={useTenantDetail(props.api)} label={t('setup.ordersTitle')} height="320px">
      {(tenant) => <OrdersForm {...props} tenant={tenant} />}
    </QueryView>
  );
};
